import { fromJS, List, Map } from 'immutable'
import {
  CASHBUYERS_SEARCH_START,
  CASHBUYERS_SEARCH_SUCCESS,
  CASHBUYERS_SEARCH_ERROR,
  CASHBUYERS_SELECT_INVESTOR,
  CASHBUYERS_SET_SORT,
  CASHBUYERS_SET_RADIUS,
  CASHBUYERS_SET_TAKE,
  CASHBUYERS_SET_SUBJECT,
  CASHBUYERS_SET_CLASSIFICATION,
  CASHBUYERS_SET_PROPERTY_TYPES,
  CASHBUYERS_TOGGLE_INVESTOR_CHECKED,
  CASHBUYERS_CLEAR_CHECKED_INVESTORS,
  CASHBUYERS_CLEAR,
} from './actions'

const initialState = fromJS({
  subjectProperty: null,
  radius: 1,
  take: 1000,
  classifications: [],
  propertyTypes: [],
  investors: [],
  properties: [],
  meta: {},
  selectedInvestorId: null,
  checkedInvestorIds: [],
  sortBy: 'matchScore',
  loading: false,
  error: null,
})

function computeMatchScore(inv) {
  const acq12 = inv.acquisitions?.totalAcquisitionCountLast12Months || 0
  const holdCount = inv.currentHoldings?.totalPropertyCount || 0
  const cashCount = inv.currentHoldings?.cashPurchasePropertyCount || 0
  const financedCount = inv.currentHoldings?.financedPropertyCount || 0
  const total = cashCount + financedCount
  const cashPct = total > 0 ? (cashCount / total) * 100 : 0
  const avgOwnership = inv.currentHoldings?.avgOwnershipLengthMonths || 0
  const disp12 = inv.dispositions?.totalDispositionCountLast12Months || 0
  let strategy = 'Investor'
  if (avgOwnership > 0 && avgOwnership < 18 && disp12 > 0) strategy = 'Fix & Flip'
  else if (avgOwnership >= 36) strategy = 'Buy & Hold'
  else if (holdCount > 5 && acq12 >= 2) strategy = 'Portfolio Builder'
  return Math.min(100, Math.round(
    Math.min(acq12 * 5, 30) + Math.min(holdCount * 2, 20) +
    (cashPct / 100) * 20 + 15 + (strategy !== 'Investor' ? 15 : 5)
  ))
}

function getVal(inv, path) {
  const parts = path.split('.')
  let val = inv
  for (const p of parts) {
    if (val == null) return 0
    val = val[p]
  }
  return Number(val) || 0
}

function sortInvestors(investors, sortBy) {
  const list = investors.toJS ? investors.toJS() : Array.isArray(investors) ? investors : []
  if (list.length === 0) return fromJS(list)

  const sorted = [...list].sort((a, b) => {
    switch (sortBy) {
      case 'matchScore': return computeMatchScore(b) - computeMatchScore(a)
      case 'activity': return getVal(b, 'acquisitions.totalAcquisitionCountLast12Months') - getVal(a, 'acquisitions.totalAcquisitionCountLast12Months')
      case 'portfolio': return getVal(b, 'currentHoldings.totalPropertyCount') - getVal(a, 'currentHoldings.totalPropertyCount')
      case 'avgPrice': return getVal(b, 'currentHoldings.avgPurchasePrice') - getVal(a, 'currentHoldings.avgPurchasePrice')
      default: return 0
    }
  })
  return fromJS(sorted)
}

export default function cashbuyersReducer(state = initialState, action) {
  switch (action.type) {
    case CASHBUYERS_SET_SUBJECT:
      return state.set('subjectProperty', fromJS(action.payload))
    case CASHBUYERS_SET_RADIUS:
      return state.set('radius', action.payload)
    case CASHBUYERS_SET_TAKE:
      return state.set('take', action.payload)
    case CASHBUYERS_SET_CLASSIFICATION:
      return state.set('classifications', fromJS(action.payload))
    case CASHBUYERS_SET_PROPERTY_TYPES:
      return state.set('propertyTypes', fromJS(action.payload))
    case CASHBUYERS_SELECT_INVESTOR:
      return state.set('selectedInvestorId', action.payload)
    case CASHBUYERS_TOGGLE_INVESTOR_CHECKED: {
      const current = state.get('checkedInvestorIds') || List()
      const id = action.payload
      const idx = current.indexOf(id)
      const next = idx >= 0 ? current.delete(idx) : current.push(id)
      return state.set('checkedInvestorIds', next)
    }
    case CASHBUYERS_CLEAR_CHECKED_INVESTORS:
      return state.set('checkedInvestorIds', List())
    case CASHBUYERS_SET_SORT:
      return state
        .set('sortBy', action.payload)
        .set('investors', sortInvestors(state.get('investors'), action.payload))
    case CASHBUYERS_SEARCH_START:
      return state.set('loading', true).set('error', null)
    case CASHBUYERS_SEARCH_SUCCESS:
      return state
        .set('loading', false)
        .set('investors', sortInvestors(fromJS(action.payload.investors), state.get('sortBy')))
        .set('properties', fromJS(action.payload.properties))
        .set('meta', fromJS(action.payload.meta))
        .set('subjectCoords', action.payload.subjectCoords ? fromJS(action.payload.subjectCoords) : null)
        .set('checkedInvestorIds', List())
        .set('error', null)
    case CASHBUYERS_SEARCH_ERROR:
      return state.set('loading', false).set('error', action.payload)
    case CASHBUYERS_CLEAR:
      return initialState
    default:
      return state
  }
}
